import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import BaseDatePicker from 'common/components/base-date-picker';
import BaseDialog from 'common/components/base-dialog';
import { BaseSelectField } from 'common/components/base-select-field';
import BaseTextField from 'common/components/base-text-field';
import FormProvider from 'common/components/form-provider';
import RequiredLabel from 'common/components/required-label';
import { campaignSchema } from 'common/schema';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    campaignDetail,
    createCampaign,
    downloadFileError,
    editCampaign,
    importQuestionExcel,
} from 'services/campaign.service';
import { ICampaignForm } from 'types/campaign';
import { createStyleSheet, handleError } from 'utils/helpers';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';
import {
    colors,
    FORMAT,
    styleInput,
    urlExportQuestionTemplate,
    urlGuideUpload,
} from 'common/constants';
import { ActionType, IBodyResponse } from 'types/type.global';
import { BaseCheckbox } from 'common/components/base-checkbox-group';
import BaseFileUpload from 'common/components/base-file-upload';
import CloseIcon from '@mui/icons-material/Close';
import { common } from '@mui/material/colors';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignsResponseFormatter } from 'utils/campaign_helpers';
import { pushNewCreatedCampaignToTopOfList } from 'slices/campaignsSlice';
import { setCampaign } from 'slices/campaignsSlice';
import BaseRadioGroup from 'common/components/base-radio-group';
import BaseSwitch from 'common/components/base-switch';
import BaseNumberField from 'common/components/base-number-field';

interface CampaignFormProps {
    open?: boolean;
    onClose?: () => void;
    actionType: ActionType;
}
const CampaignForm: React.FC<CampaignFormProps> = ({
    open,
    onClose,
    actionType,
}) => {
    const navigate = useNavigate();
    const userInfo = useAppSelector((state) => state.user);
    const [uploadResponse, setUploadResponse] =
        React.useState<IBodyResponse<null>>();
    const [errorFileUpload, setErrorFileUpload] = React.useState<string>('');
    const [existingLogo, setExistingLogo] = React.useState<string>('');
    const [existingEmailTemplate, setExistingEmailTemplate] =
        React.useState<string>('');
    const [existingLogoIdentifier, setExistingLogoIdentifier] =
        React.useState<string>('');
    const [
        existingEmailTemplateIdentifier,
        setExistingEmailTemplateIdentifier,
    ] = React.useState<string>('');

    const campaignId = useParams<{ id: string }>().id;
    const methods = useForm({
        resolver: yupResolver(campaignSchema),
        defaultValues: {
            title: '',
            public_date: undefined,
            close_date: undefined,
            secure_type: 'public',
            // admin_id: userInfo?.attributes.role === 'client' ? userInfo.id : '',
            description: '',
            import_question: false,
            file: '',
            email_template: '',
            logo: '',
            kind: 'normal',
            enable_minimum_answer_time: false,
            minimum_answer_time: 0,
            is_multiple_answer: false,
            is_required_location: false,
            is_public_preview: false,
        },
        mode: 'onChange',
    });
    const { t } = useTranslation();
    const {
        handleSubmit,
        setError,
        watch,
        resetField,
        trigger,
        setValue,
        formState: { isSubmitting, errors },
    } = methods;

    const dispatch = useAppDispatch();
    const file = watch('file');
    const emailTemplate = watch('email_template');
    const logo = watch('logo');
    const secureType = watch('secure_type');
    const kind = watch('kind');

    useEffect(() => {
        if (!kind) {
            setValue('secure_type', 'public');
        }
    }, [kind, setValue]);

    useEffect(() => {
        if (secureType === 'secret' || secureType === 'public') {
            setValue('is_multiple_answer', true);
        } else if (secureType === 'secret_once') {
            setValue('is_multiple_answer', false);
        }
    }, [secureType, setValue]);

    const handleDlgClose = (event?: unknown, reason?: string) => {
        if (reason && reason === 'backdropClick') {
            return;
        }
        setUploadResponse(undefined);
        setErrorFileUpload('');
        setExistingLogo('');
        setExistingEmailTemplate('');
        onClose && onClose();
    };
    const onSubmit = handleSubmit(async (data) => {
        const formData = new FormData();
        formData.append('survey[title]', data.title);
        formData.append('survey[kind]', data.kind);
        // formData.append(
        //     'survey[admin_id]',
        //     userInfo?.attributes.role === 'client' ? userInfo.id : data.admin_id
        // );
        formData.append(
            'survey[public_date]',
            dayjs(data.public_date).format(FORMAT.date.DMY)
        );
        formData.append(
            'survey[close_date]',
            dayjs(data.close_date).format(FORMAT.date.DMY)
        );
        formData.append('survey[description]', data.description);
        formData.append('survey[secure_type]', data.secure_type);
        formData.append(
            'survey[enable_minimum_answer_time]',
            data.enable_minimum_answer_time ? 'true' : 'false'
        );
        formData.append(
            'survey[minimum_answer_time]',
            data.minimum_answer_time ? data.minimum_answer_time.toString() : '0'
        );
        formData.append(
            'survey[is_multiple_answer]',
            data.is_multiple_answer ? 'true' : 'false'
        );
        formData.append(
            'survey[is_required_location]',
            data.is_required_location ? 'true' : 'false'
        );
        formData.append(
            'survey[is_public_preview]',
            data.is_public_preview ? 'true' : 'false'
        );
        if (data.import_question) {
            formData.append('survey[file]', data.file as File);
        }
        if (data.secure_type === 'unique_link') {
            if (data.email_template && data.email_template instanceof File) {
                formData.append(
                    'survey[email_template]',
                    data.email_template as File
                );
            }
            if (data.logo && data.logo instanceof File) {
                formData.append('survey[logo]', data.logo as File);
            }
        }

        try {
            dispatch(setIsLoading(true));
            const isValid = await trigger('file');
            if (!isValid) {
                return;
            }
            const response =
                actionType === 'add'
                    ? await createCampaign(formData)
                    : await editCampaign(formData, Number(campaignId));
            if (response.success) {
                if (response.data) {
                    if (actionType === 'add') {
                        dispatch(
                            pushNewCreatedCampaignToTopOfList(
                                campaignsResponseFormatter(response.data, t)
                            )
                        );
                        navigate(
                            `/admin/campaign/overview/${response.data.id}`
                        );
                    } else {
                        dispatch(
                            setCampaign(
                                campaignsResponseFormatter(response.data, t)
                            )
                        );
                    }
                }
                toast.success(response.message);
                handleDlgClose();
            } else {
                toast.error(response.message);
                Object.entries(response.message).forEach(([key, value]) => {
                    setError(key as keyof ICampaignForm, {
                        type: 'manual',
                        message: value,
                    });
                });
            }
        } catch (error) {
            handleError(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    });

    const startDate = watch('public_date');
    const endDate = watch('close_date');
    useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('close_date', '');
            }
        }
    }, [startDate, endDate, setValue]);

    React.useEffect(() => {
        const uploadFileQuestionExcel = async () => {
            if (!file) return;
            const isValid = await trigger('file');
            if (!isValid) return;
            dispatch(setIsLoading(true));
            const formData = new FormData();
            formData.append('survey[file]', file as File);
            try {
                const response = await importQuestionExcel(formData);
                if (response.success) {
                    setErrorFileUpload(response.message);
                    setUploadResponse(response);
                    dispatch(setIsLoading(false));
                } else {
                    const newError = {
                        message: '',
                        data: response.data,
                        status_code: response.status_code,
                        success: response.success,
                    };
                    setUploadResponse(newError);
                    Object.entries(response.message).forEach(([key, value]) => {
                        setErrorFileUpload(value);
                        setError(key as keyof ICampaignForm, {
                            type: 'manual',
                            message: value,
                        });
                    });
                }
            } catch (error) {
                handleError(error);
                dispatch(setIsLoading(false));
            } finally {
                dispatch(setIsLoading(false));
            }
        };
        uploadFileQuestionExcel();
    }, [file, trigger, dispatch, setError]);

    const customerOptions = [
        {
            label: 'Hungtt',
            value: 25,
        },
        {
            label: 'Thend1',
            value: 20,
        },
        {
            label: 'Namnv1',
            value: 30,
        },
    ];
    useEffect(() => {
        const fetchData = async () => {
            if (actionType === 'edit') {
                try {
                    const response = await campaignDetail(Number(campaignId));
                    if (response.success) {
                        const data = response.data.attributes;
                        setExistingLogo(data.logo_url ?? '');
                        setExistingEmailTemplate(data.email_template_url ?? '');
                        setExistingLogoIdentifier(data.logo_identifier ?? '');
                        setExistingEmailTemplateIdentifier(
                            data.email_template_identifier ?? ''
                        );

                        methods.reset({
                            title: data.title,
                            public_date: data.public_date ?? '',
                            close_date: data.close_date ?? '',
                            description: data.description ?? undefined,
                            kind: data.kind ?? 'normal',
                            secure_type: data.secure_type ?? 'public',
                            enable_minimum_answer_time:
                                data.enable_minimum_answer_time ?? false,
                            minimum_answer_time: data.minimum_answer_time ?? 0,
                            is_multiple_answer:
                                data.is_multiple_answer ?? false,
                            is_required_location:
                                data.is_required_location ?? false,
                            is_public_preview: data.is_public_preview ?? false,
                            logo: data.logo_url
                                ? {
                                      isExisting: true,
                                      name: data.logo_identifier ?? 'logo',
                                  }
                                : '',
                            email_template: data.email_template_url
                                ? {
                                      isExisting: true,
                                      name:
                                          data.email_template_identifier ??
                                          'email_template',
                                  }
                                : '',
                            // admin_id:
                            //     userInfo?.attributes.role === 'client'
                            //         ? userInfo.id
                            //         : '1',
                        });
                    }
                } catch (error) {
                    handleError(error);
                }
            }
        };
        fetchData();
    }, [
        t,
        campaignId,
        actionType,
        methods,
        userInfo?.attributes.role,
        userInfo?.id,
    ]);

    const renderFileName = () => {
        if (file instanceof File) {
            return file.name;
        }
        return 'No file selected';
    };

    const renderEmailTemplateName = () => {
        if (emailTemplate instanceof File) {
            return emailTemplate.name || '';
        }
        if (
            emailTemplate &&
            typeof emailTemplate === 'object' &&
            'name' in emailTemplate
        ) {
            return emailTemplate.name || '';
        }
        return 'No file selected';
    };

    const renderEmailTemplateError = () => {
        if (errors.email_template) {
            return (
                <Box
                    sx={{
                        marginTop: '5px',
                        fontSize: '14px',
                        marginLeft: 0,
                        color: `${colors.textError} !important`,
                        fontWeight: '400',
                    }}
                >
                    {errors?.email_template?.message}
                </Box>
            );
        }
        return null;
    };

    const renderLogoName = () => {
        if (logo instanceof File) {
            return logo.name || '';
        }
        if (logo && typeof logo === 'object' && 'name' in logo) {
            return logo.name || '';
        }
        return 'No file selected';
    };

    const renderLogoError = () => {
        if (errors.logo) {
            return (
                <Box
                    sx={{
                        marginTop: '5px',
                        fontSize: '14px',
                        marginLeft: 0,
                        color: `${colors.textError} !important`,
                        fontWeight: '400',
                    }}
                >
                    {errors?.logo?.message}
                </Box>
            );
        }
        return null;
    };

    const renderFileError = () => {
        if (errors.file) {
            return (
                <Box
                    sx={{
                        marginTop: '5px',
                        fontSize: '14px',
                        marginLeft: 0,
                        color: `${colors.textError} !important`,
                        fontWeight: '400',
                    }}
                >
                    {errors?.file?.message}
                </Box>
            );
        }
        return null;
    };

    const handleDownloadFileError = async () => {
        try {
            const response = await downloadFileError();
            if (!response.success) {
                toast.error(response.message);
                return;
            }
            const link = document.createElement('a');
            link.href = response.data.file_url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            handleError(error);
        }
    };

    const renderCampaignType = (kind: string) => {
        switch (kind) {
            case 'page_logic':
                return 'Chia trang';
            case 'public_with_code':
                return 'Đánh giá';
            case 'normal':
                return 'Thông thường';
            default:
                return '';
        }
    };

    const renderSecureType = (secureType: string) => {
        switch (secureType) {
            case 'secret':
                return '1 Lần';
            case 'secret_once':
                return '2 Lần';
            case 'unique_link':
                return 'Link duy nhất';
            case 'public':
                return 'Công khai';
            default:
                return '';
        }
    };

    const isDisableMultiAnswerInput = React.useMemo(() => {
        return actionType === 'edit' || secureType !== 'unique_link';
    }, [actionType, secureType]);

    return (
        <FormProvider methods={methods} onSubmit={onSubmit}>
            <BaseDialog
                maxWidth={'md'}
                open={open || false}
                onClose={handleDlgClose}
                fullWidth
                sxContent={{
                    padding: '45px',
                }}
                title={
                    actionType === 'add'
                        ? 'Thêm mới chiến dịch'
                        : 'Chỉnh sửa chiến dịch'
                }
                PaperProps={{
                    component: 'form',
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <Stack spacing={'7px'}>
                            <RequiredLabel
                                component={'span'}
                                label={t('common.campaign.title')}
                                required
                                sx={{ fontWeight: 600 }}
                            />
                            <BaseTextField
                                autoComplete="off"
                                name="title"
                                size="small"
                                fullWidth
                                placeholder={t('placeholder.campaign.name')}
                                inputProps={{
                                    maxLength: 500,
                                }}
                                sx={{
                                    ...styleInput,
                                }}
                            />
                        </Stack>
                        <Stack spacing={'7px'} mt={2}>
                            <RequiredLabel
                                component={'span'}
                                label={'Loại chiến dịch'}
                                required
                                sx={{ fontWeight: 600 }}
                            />
                            {actionType !== 'edit' ? (
                                <BaseRadioGroup
                                    name={'kind'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                    }}
                                    options={[
                                        {
                                            label: 'Thông thường',
                                            value: 'normal',
                                        },
                                        {
                                            label: 'Chia trang',
                                            value: 'page_logic',
                                        },
                                        {
                                            label: 'Đánh giá',
                                            value: 'public_with_code',
                                        },
                                    ]}
                                />
                            ) : (
                                <p>{renderCampaignType(watch('kind'))}</p>
                            )}
                        </Stack>
                        {watch('kind') && (
                            <Stack spacing={'7px'} mt={2}>
                                <RequiredLabel
                                    component={'span'}
                                    label={'Loại bảo mật'}
                                    required
                                    sx={{ fontWeight: 600 }}
                                />
                                {actionType !== 'edit' ? (
                                    <BaseRadioGroup
                                        name={'secure_type'}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                        }}
                                        options={[
                                            {
                                                label: '1 Lần',
                                                value: 'secret',
                                            },
                                            {
                                                label: '2 Lần',
                                                value: 'secret_once',
                                            },
                                            {
                                                label: 'Link duy nhất',
                                                value: 'unique_link',
                                            },
                                            {
                                                label: 'Công khai',
                                                value: 'public',
                                            },
                                        ]}
                                    />
                                ) : (
                                    <p>
                                        {renderSecureType(watch('secure_type'))}
                                    </p>
                                )}
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack spacing={'7px'}>
                            <RequiredLabel
                                component={'span'}
                                label={'Số lượt trả lời'}
                                required
                                sx={{ fontWeight: 600 }}
                            />
                            <BaseRadioGroup
                                name={'is_multiple_answer'}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                }}
                                options={[
                                    {
                                        label: 'Trả lời 1 lần',
                                        value: false,
                                    },
                                    {
                                        label: 'Trả lời nhiều lần',
                                        value: true,
                                    },
                                ]}
                                disabled={isDisableMultiAnswerInput}
                            />
                        </Stack>
                    </Grid>
                    {userInfo?.attributes.role === 'admin' && (
                        <Grid item xs={12} md={12}>
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    component={'span'}
                                    label={t('common.customer')}
                                    required
                                    sx={{ fontWeight: 600 }}
                                />
                                <Stack direction={'row'} gap={1}>
                                    <BaseSelectField
                                        name="admin_id"
                                        size="small"
                                        defaultValue={20}
                                    >
                                        {customerOptions.map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </BaseSelectField>
                                </Stack>
                            </Stack>
                        </Grid>
                    )}
                    {secureType === 'unique_link' && (
                        <Grid item xs={12} md={12}>
                            <Stack spacing={'7px'} display={'block'}>
                                <RequiredLabel
                                    component={'span'}
                                    label={'Mẫu email mời trả lời'}
                                    required={false}
                                    sx={{ fontWeight: 600 }}
                                />
                                {actionType === 'edit' &&
                                    existingEmailTemplate &&
                                    !(emailTemplate instanceof File) && (
                                        <Box
                                            sx={{
                                                padding: '10px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '5px',
                                                marginBottom: '10px',
                                            }}
                                        >
                                            <Typography variant="body2">
                                                File hiện tại:{' '}
                                                <a
                                                    href={existingEmailTemplate}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: colors.main,
                                                        textDecoration:
                                                            'underline',
                                                    }}
                                                >
                                                    {existingEmailTemplateIdentifier ||
                                                        'Xem file'}
                                                </a>
                                            </Typography>
                                        </Box>
                                    )}
                                {emailTemplate &&
                                emailTemplate instanceof File ? (
                                    <>
                                        <Typography>
                                            {String(renderEmailTemplateName())}
                                        </Typography>
                                        <IconButton>
                                            <CloseIcon
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    resetField(
                                                        'email_template'
                                                    );
                                                }}
                                            />
                                        </IconButton>
                                    </>
                                ) : (
                                    <BaseFileUpload
                                        name="email_template"
                                        multiple={false}
                                        sx={{
                                            width: '100%',
                                            border: `2px dashed ${colors.main}`,
                                            backgroundColor: '#f4f4f4',
                                        }}
                                        description=""
                                        uploadLabel="Tải file mẫu"
                                        accept={{
                                            'text/html': ['.html'],
                                        }}
                                    />
                                )}
                                {renderEmailTemplateError()}
                            </Stack>
                        </Grid>
                    )}
                    {secureType === 'unique_link' && (
                        <Grid item xs={12} md={12}>
                            <Stack spacing={'7px'} display={'block'}>
                                <RequiredLabel
                                    component={'span'}
                                    label={'Logo hiển thị trong email'}
                                    required={false}
                                    sx={{ fontWeight: 600 }}
                                />
                                {actionType === 'edit' &&
                                    existingLogo &&
                                    !(logo instanceof File) && (
                                        <Box
                                            sx={{
                                                padding: '10px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '5px',
                                                marginBottom: '10px',
                                            }}
                                        >
                                            <Typography variant="body2">
                                                Logo hiện tại:{' '}
                                                <a
                                                    href={existingLogo}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: colors.main,
                                                        textDecoration:
                                                            'underline',
                                                    }}
                                                >
                                                    {existingLogoIdentifier ||
                                                        'Xem logo'}
                                                </a>
                                            </Typography>
                                        </Box>
                                    )}
                                {logo && logo instanceof File ? (
                                    <>
                                        <Typography>
                                            {String(renderLogoName())}
                                        </Typography>
                                        <IconButton>
                                            <CloseIcon
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    resetField('logo');
                                                }}
                                            />
                                        </IconButton>
                                    </>
                                ) : (
                                    <BaseFileUpload
                                        name="logo"
                                        multiple={false}
                                        sx={{
                                            width: '100%',
                                            border: `2px dashed ${colors.main}`,
                                            backgroundColor: '#f4f4f4',
                                        }}
                                        description=""
                                        uploadLabel="Tải file logo"
                                        accept={{
                                            'image/png': ['.png'],
                                        }}
                                    />
                                )}
                                {renderLogoError()}
                            </Stack>
                        </Grid>
                    )}
                    <Grid item xs={12} md={12}>
                        <Stack
                            spacing={'7px'}
                            direction="row"
                            alignItems="center"
                        >
                            <RequiredLabel
                                component={'span'}
                                label="Giới hạn thời gian trả lời ngắn nhất"
                                sx={{ fontWeight: 600 }}
                                required={false}
                            />
                            <BaseSwitch name={`enable_minimum_answer_time`} />
                            {watch('enable_minimum_answer_time') && (
                                <>
                                    <BaseNumberField
                                        sx={{ maxWidth: '200px' }}
                                        name="minimum_answer_time"
                                        min={0}
                                        size="small"
                                    />
                                    <Typography>giây</Typography>
                                </>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack
                            spacing={'7px'}
                            direction="row"
                            alignItems="center"
                        >
                            <RequiredLabel
                                component={'span'}
                                label="Yêu cầu bật vị trí khi trả lời"
                                sx={{ fontWeight: 600 }}
                                required={false}
                            />
                            <BaseSwitch name="is_required_location" />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack
                            spacing={'7px'}
                            direction="row"
                            alignItems="center"
                        >
                            <RequiredLabel
                                component={'span'}
                                label="Cho phép xem trước công khai"
                                sx={{ fontWeight: 600 }}
                                required={false}
                            />
                            <BaseSwitch name="is_public_preview" />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack spacing={'7px'}>
                            <RequiredLabel
                                component={'span'}
                                label={t('common.time')}
                                required
                                sx={{ fontWeight: 600 }}
                            />
                            <Stack direction={'row'} gap={1}>
                                <BaseDatePicker
                                    name="public_date"
                                    size="small"
                                    placeholder={t('common.date.from')}
                                    sx={styleInput}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                />
                                <BaseDatePicker
                                    name="close_date"
                                    size="small"
                                    placeholder={t('common.date.to')}
                                    sx={styleInput}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Stack spacing={'7px'}>
                            <RequiredLabel
                                component={'span'}
                                label={t('common.description')}
                                required={true}
                                sx={{ fontWeight: 600 }}
                            />
                            <BaseTextField
                                multiline
                                rows={4}
                                autoComplete="off"
                                name="description"
                                fullWidth
                                inputProps={{
                                    maxLength: 500,
                                }}
                                placeholder={t(
                                    'placeholder.campaign.description'
                                )}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        ...styleInput['& .MuiInputBase-root'],
                                        height: '130px',
                                        borderRadius: '15px',
                                    },
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <BaseCheckbox
                            name="import_question"
                            label={'Import bảng câu hỏi'}
                        />
                    </Grid>
                    {watch('import_question') && (
                        <>
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={styles.guideText}>
                                        <a href={urlGuideUpload}>
                                            Hướng dẫn upload file mẫu
                                        </a>
                                    </Box>
                                    <Button
                                        href={urlExportQuestionTemplate}
                                        variant="contained"
                                        sx={styles.btnUpload}
                                        startIcon={<IosShareOutlinedIcon />}
                                    >
                                        Xuất mẫu
                                    </Button>
                                </Box>
                            </Grid>
                            {!watch('file') && !uploadResponse ? (
                                <Grid item xs={12}>
                                    <BaseFileUpload
                                        name="file"
                                        multiple={false}
                                        sx={{
                                            width: '100%',
                                            border: `2px dashed ${colors.main}`,
                                            backgroundColor: '#f4f4f4',
                                        }}
                                    />
                                    {renderFileError()}
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            minHeight: '80px',
                                            background: `${(!uploadResponse?.success && uploadResponse !== undefined) || !errorFileUpload ? '#EF465C0D' : 'var(--color-primary-light-20)'}`,
                                            padding: '24px 35px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            border: `1px solid ${(!uploadResponse?.success && uploadResponse !== undefined) || !errorFileUpload ? '#F52A37' : colors.main}`,
                                            borderRadius: '10px',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: common.black,
                                            }}
                                        >
                                            <Box>
                                                {(!uploadResponse?.success &&
                                                    uploadResponse !==
                                                        undefined) ||
                                                !errorFileUpload ? (
                                                    <WarningAmberIcon
                                                        sx={{
                                                            color: 'warning.main',
                                                        }}
                                                    />
                                                ) : (
                                                    <InsertDriveFileOutlinedIcon
                                                        sx={{
                                                            color: 'var(--color-primary)',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box>
                                                <Box
                                                    sx={{
                                                        fontSize: '20px',
                                                        color: common.black,
                                                    }}
                                                >
                                                    {renderFileName()}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: `${
                                                            !uploadResponse?.success &&
                                                            uploadResponse
                                                                ? common.black
                                                                : '#13B75BB2'
                                                        }`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {errorFileUpload}
                                                    {!errorFileUpload &&
                                                        renderFileError()}
                                                    <Typography
                                                        component={'a'}
                                                        sx={{
                                                            color: colors.main,
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            textDecoration:
                                                                'none',
                                                        }}
                                                    >
                                                        {!uploadResponse?.success &&
                                                        uploadResponse !==
                                                            undefined ? (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                                onClick={() =>
                                                                    handleDownloadFileError()
                                                                }
                                                            >
                                                                Kiểm tra file
                                                                lỗi
                                                                <FileDownloadOutlinedIcon />
                                                            </Box>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            sx={{
                                                width: '33px',
                                                height: '33px',
                                                background: '#FF0000',
                                                '&:hover': {
                                                    background: '#FF0000',
                                                },
                                            }}
                                            onClick={() => {
                                                resetField('file');
                                                setUploadResponse(undefined);
                                                setErrorFileUpload('');
                                            }}
                                        >
                                            <CloseIcon
                                                sx={{ color: common.white }}
                                            />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item xs={12} md={12}>
                        <Stack
                            spacing={1}
                            direction={'row'}
                            justifyContent={'center'}
                        >
                            <Button
                                variant="outlined"
                                sx={styles.modalButton}
                                onClick={() => handleDlgClose()}
                            >
                                {t('page.campaign.modal.copy.button_cancel')}
                            </Button>
                            <Button
                                disabled={isSubmitting}
                                type="submit"
                                variant="contained"
                                sx={styles.modalButton}
                            >
                                {actionType === 'add' ? 'Tạo' : 'Cập nhật'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </BaseDialog>
        </FormProvider>
    );
};
const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '60px',
        minHeight: '60px',
        minWidth: '155px',
        fontSize: '20px',
        fontWeight: '700',
    },
    guideText: {
        cursor: 'pointer',
        textDecoration: 'underline',
        color: colors.main,
        fontSize: '14px',
        fontWeight: '500',
    },
    btnUpload: {
        height: '53px',
        width: '175px',
        borderRadius: '60px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 700,
    },
});
export default CampaignForm;
